import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useCapabilities } from '../../hooks/useCapabilities';
import { CheckCircle2, Edit2, Megaphone, Plus, Save, Search, Trash2, X, XCircle } from 'lucide-react';

const CONTEXT_OPTIONS = [
    { value: 'any', label: 'Ambos' },
    { value: 'with_clid', label: 'Con CLID' },
    { value: 'without_clid', label: 'Sin CLID' }
];

const PAYLOAD_TARGET_OPTIONS = [
    { value: 'zoho_lead', label: 'Campo Zoho' },
    { value: 'assign_tag', label: 'Asignar etiqueta' },
    { value: 'remove_tag', label: 'Quitar etiqueta' }
];

const PROTECTED_FIELD_NAMES = new Set(['External_ID', 'gclid', 'Phone', 'First_Name', 'Last_Name']);
const PROTECTED_FIELD_NAMES_LOWER = new Set([...PROTECTED_FIELD_NAMES].map(fieldName => fieldName.toLowerCase()));

const emptyField = (sortOrder = 0) => ({
    id: null,
    applies_when: 'any',
    payload_target: 'zoho_lead',
    field_name: '',
    field_value: '',
    sort_order: sortOrder,
    active: true
});

const emptyRule = () => ({
    id: null,
    phrase: '',
    active: true,
    fields: []
});

const contextLabel = (value) => CONTEXT_OPTIONS.find(option => option.value === value)?.label || value;

const normalizeRule = (rule) => ({
    ...rule,
    fields: (rule.fields || [])
        .filter(field => field.active !== false)
        .map(field => ({
            id: field.id || null,
            applies_when: field.applies_when || 'any',
            payload_target: field.payload_target || 'zoho_lead',
            field_name: field.field_name || '',
            field_value: field.field_value || '',
            sort_order: Number.isFinite(Number(field.sort_order)) ? Number(field.sort_order) : 0,
            active: true
        }))
});

const WhatsAppMessageAdRules = () => {
    const { authFetch } = useAuth();
    const { hasCapability } = useCapabilities();
    const [rules, setRules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [availableTags, setAvailableTags] = useState([]);

    const canManage = hasCapability('whatsapp_admin');

    const reload = useCallback(async () => {
        if (!canManage) return;
        setLoading(true);
        setError(null);
        try {
            const res = await authFetch('/api/backoffice/whatsapp-message-ad-rules');
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setError(data.error || `Error ${res.status}`);
                return;
            }
            setRules((data.rules || []).map(normalizeRule));
        } catch (err) {
            setError(err.message || 'Error cargando reglas');
        } finally {
            setLoading(false);
        }
    }, [authFetch, canManage]);

    useEffect(() => {
        reload();
    }, [reload]);

    useEffect(() => {
        // Cargar etiquetas disponibles
        const loadTags = async () => {
            if (!canManage) return;
            try {
                const res = await authFetch('/api/tags/');
                if (res.ok) {
                    const data = await res.json();
                    // El endpoint devuelve un array directamente, no un objeto con propiedad tags
                    setAvailableTags(Array.isArray(data) ? data : []);
                }
            } catch (err) {
                console.error('Error cargando etiquetas:', err);
            }
        };
        loadTags();
    }, [authFetch, canManage]);

    const filteredRules = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        if (!term) return rules;
        return rules.filter(rule => {
            const haystack = [
                rule.phrase,
                ...(rule.fields || []).flatMap(field => [
                    field.applies_when,
                    field.payload_target,
                    field.field_name,
                    field.field_value
                ])
            ].join(' ').toLowerCase();
            return haystack.includes(term);
        });
    }, [rules, searchTerm]);

    const validate = (rule) => {
        if (!rule.phrase.trim()) return 'La frase es obligatoria';

        const seen = new Set();
        for (const field of rule.fields || []) {
            const fieldName = (field.field_name || '').trim();
            const fieldValue = (field.field_value || '').trim();
            if (!fieldName) return 'El nombre del campo es obligatorio';
            if (!fieldValue) return 'El valor del campo es obligatorio';
            if (PROTECTED_FIELD_NAMES_LOWER.has(fieldName.toLowerCase())) return `${fieldName} se gestiona desde código`;

            const key = `${field.applies_when || 'any'}|${field.payload_target || 'zoho_lead'}|${fieldName}`;
            if (seen.has(key)) return 'Hay campos duplicados para el mismo contexto y destino';
            seen.add(key);
        }
        return null;
    };

    const buildPayload = (rule) => ({
        phrase: rule.phrase.trim(),
        active: !!rule.active,
        fields: (rule.fields || [])
            .map(field => ({
                id: field.id || null,
                applies_when: field.applies_when || 'any',
                payload_target: (field.payload_target || 'zoho_lead').trim(),
                field_name: (field.field_name || '').trim(),
                field_value: (field.field_value || '').trim(),
                sort_order: Number(field.sort_order) || 0,
                active: field.active !== false
            }))
    });

    const save = async () => {
        const validationError = validate(editing);
        if (validationError) {
            setError(validationError);
            return;
        }

        setSaving(true);
        setError(null);
        try {
            const url = editing.id
                ? `/api/backoffice/whatsapp-message-ad-rules/${editing.id}`
                : '/api/backoffice/whatsapp-message-ad-rules';
            const method = editing.id ? 'PUT' : 'POST';
            const res = await authFetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(buildPayload(editing))
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setError(data.error || `Error ${res.status}`);
                return;
            }
            const savedRule = normalizeRule(data);
            setRules(current => {
                const exists = current.some(rule => rule.id === savedRule.id);
                if (!exists) return [savedRule, ...current];
                return current.map(rule => rule.id === savedRule.id ? savedRule : rule);
            });
            setEditing(null);
            await reload();
        } catch (err) {
            setError(err.message || 'Error guardando regla');
        } finally {
            setSaving(false);
        }
    };

    const toggleActive = async (rule) => {
        setError(null);
        const res = await authFetch(`/api/backoffice/whatsapp-message-ad-rules/${rule.id}/active`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ active: !rule.active })
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
            setError(data.error || `Error ${res.status}`);
            return;
        }
        setRules(current => current.map(item => item.id === rule.id ? normalizeRule(data) : item));
    };

    const updateEditingField = (index, changes) => {
        setEditing(current => ({
            ...current,
            fields: current.fields.map((field, fieldIndex) => (
                fieldIndex === index ? { ...field, ...changes } : field
            ))
        }));
    };

    const addEditingField = () => {
        const maxOrder = Math.max(0, ...(editing.fields || []).map(field => Number(field.sort_order) || 0));
        setEditing(current => ({
            ...current,
            fields: [...(current.fields || []), emptyField(maxOrder + 10)]
        }));
    };

    const removeEditingField = (index) => {
        setEditing(current => ({
            ...current,
            fields: current.fields.filter((_, fieldIndex) => fieldIndex !== index)
        }));
    };

    if (!canManage) {
        return (
            <div className="p-8 border border-slate-800 rounded-lg text-slate-400 text-sm">
                No tienes permiso para configurar reglas WhatsApp Ads.
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 bg-slate-900/50 p-6 rounded-lg border border-slate-800">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <Megaphone className="text-primary" size={22} />
                        Reglas WhatsApp Ads
                    </h1>
                </div>
                <button
                    onClick={() => { setEditing(emptyRule()); setError(null); }}
                    className="flex items-center justify-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-lg font-medium hover:bg-primary/90 transition-colors"
                >
                    <Plus size={18} />
                    Nueva regla
                </button>
            </div>

            {error && (
                <div className="p-3 bg-rose-500/10 border border-rose-500/30 rounded-lg text-rose-300 text-sm">
                    {error}
                </div>
            )}

            <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden shadow-xl">
                <div className="p-4 border-b border-slate-800 bg-slate-950/50">
                    <div className="relative max-w-xl">
                        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                        <input
                            type="text"
                            placeholder="Buscar por frase o campo..."
                            className="w-full bg-slate-900 border border-slate-800 rounded-lg pl-10 pr-4 py-2 text-sm text-slate-200 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/50"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                <div className="hidden lg:grid grid-cols-12 gap-4 p-4 border-b border-slate-800 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-900/80">
                    <div className="col-span-5">Frase</div>
                    <div className="col-span-4">Campos</div>
                    <div className="col-span-1 text-center">Estado</div>
                    <div className="col-span-2 text-right pr-3">Acciones</div>
                </div>

                <div className="divide-y divide-slate-800/50">
                    {loading ? (
                        <div className="p-8 text-center text-slate-500 animate-pulse">Cargando reglas...</div>
                    ) : filteredRules.length === 0 ? (
                        <div className="p-8 text-center text-slate-500">No hay reglas configuradas.</div>
                    ) : filteredRules.map(rule => (
                        <div key={rule.id} className="grid grid-cols-1 lg:grid-cols-12 gap-4 p-4 items-center hover:bg-slate-800/30 transition-colors">
                            <div className="lg:col-span-5">
                                <div className="text-sm font-semibold text-slate-100 whitespace-pre-wrap break-words">{rule.phrase}</div>
                                <div className="text-xs text-slate-500 font-mono mt-1">{rule.id}</div>
                            </div>
                            <div className="lg:col-span-4">
                                <FieldSummary fields={rule.fields} />
                            </div>
                            <div className="lg:col-span-1 flex lg:justify-center">
                                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold uppercase tracking-wider border ${rule.active ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-slate-500/10 text-slate-400 border-slate-500/20'}`}>
                                    {rule.active ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
                                    {rule.active ? 'Activa' : 'Inactiva'}
                                </span>
                            </div>
                            <div className="lg:col-span-2 flex justify-end gap-2">
                                <button
                                    onClick={() => toggleActive(rule)}
                                    className="px-3 py-1.5 text-xs bg-slate-800 hover:bg-slate-700 rounded-lg"
                                >
                                    {rule.active ? 'Desactivar' : 'Activar'}
                                </button>
                                <button
                                    onClick={() => { setEditing(normalizeRule(rule)); setError(null); }}
                                    className="p-1.5 text-slate-400 hover:text-primary hover:bg-primary/10 rounded-lg"
                                    title="Editar"
                                >
                                    <Edit2 size={16} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {editing && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-slate-950 border border-slate-800 rounded-lg w-full max-w-5xl max-h-[90vh] overflow-y-auto">
                        <div className="p-5 border-b border-slate-800 flex items-center justify-between">
                            <h2 className="font-bold">{editing.id ? 'Editar regla' : 'Nueva regla'}</h2>
                            <button onClick={() => setEditing(null)} className="p-1 hover:bg-slate-800 rounded" title="Cerrar">
                                <X size={16} />
                            </button>
                        </div>
                        <div className="p-5 space-y-5">
                            <Field label="Frase exacta">
                                <textarea
                                    value={editing.phrase}
                                    onChange={(e) => setEditing({ ...editing, phrase: e.target.value })}
                                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm min-h-24"
                                />
                            </Field>

                            <Field label="Estado">
                                <label className="h-[38px] flex items-center gap-2 text-sm text-slate-300">
                                    <input
                                        type="checkbox"
                                        checked={!!editing.active}
                                        onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
                                    />
                                    Activa
                                </label>
                            </Field>

                            <div className="space-y-3">
                                <div className="flex items-center justify-between gap-3">
                                    <h3 className="text-sm font-semibold text-slate-200">Campos configurables</h3>
                                    <button
                                        onClick={addEditingField}
                                        className="flex items-center gap-2 px-3 py-1.5 text-xs bg-slate-800 hover:bg-slate-700 rounded-lg"
                                    >
                                        <Plus size={14} />
                                        Añadir campo
                                    </button>
                                </div>

                                <div className="hidden xl:grid grid-cols-[130px_130px_1fr_1.2fr_90px_80px_40px] gap-2 text-[11px] font-bold uppercase tracking-wider text-slate-500 px-1">
                                    <div>Contexto</div>
                                    <div>Destino</div>
                                    <div>Campo</div>
                                    <div>Valor</div>
                                    <div>Orden</div>
                                    <div>Activo</div>
                                    <div></div>
                                </div>

                                <div className="space-y-2">
                                    {(editing.fields || []).map((field, index) => {
                                        const isTagField = field.payload_target === 'assign_tag' || field.payload_target === 'remove_tag';
                                        
                                        return (
                                            <div key={field.id || index} className={`grid gap-2 items-start p-3 bg-slate-900/70 border border-slate-800 rounded-lg ${isTagField ? 'grid-cols-1 xl:grid-cols-[130px_130px_2fr_90px_80px_40px]' : 'grid-cols-1 xl:grid-cols-[130px_130px_1fr_1.2fr_90px_80px_40px]'}`}>
                                                <Select
                                                    value={field.applies_when}
                                                    onChange={(value) => updateEditingField(index, { applies_when: value })}
                                                    options={CONTEXT_OPTIONS}
                                                />
                                                <Select
                                                    value={field.payload_target}
                                                    onChange={(value) => updateEditingField(index, { payload_target: value })}
                                                    options={PAYLOAD_TARGET_OPTIONS}
                                                />
                                                {isTagField ? (
                                                    <TagFieldSelector
                                                        field={field}
                                                        onChange={(changes) => updateEditingField(index, changes)}
                                                        availableTags={availableTags}
                                                    />
                                                ) : (
                                                    <>
                                                        <Input
                                                            value={field.field_name}
                                                            onChange={(value) => updateEditingField(index, { field_name: value })}
                                                            placeholder="Campana_UTM"
                                                        />
                                                        <Input
                                                            value={field.field_value}
                                                            onChange={(value) => updateEditingField(index, { field_value: value })}
                                                            placeholder="WhatsApp"
                                                        />
                                                    </>
                                                )}
                                                <input
                                                    type="number"
                                                    value={field.sort_order}
                                                    onChange={(e) => updateEditingField(index, { sort_order: e.target.value })}
                                                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm"
                                                />
                                                <label className="h-[38px] flex items-center gap-2 text-sm text-slate-300">
                                                    <input
                                                        type="checkbox"
                                                        checked={field.active !== false}
                                                        onChange={(e) => updateEditingField(index, { active: e.target.checked })}
                                                    />
                                                    Sí
                                                </label>
                                                <button
                                                    onClick={() => removeEditingField(index)}
                                                    className="h-[38px] flex items-center justify-center text-slate-500 hover:text-rose-300 hover:bg-rose-500/10 rounded-lg"
                                                    title="Quitar"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                        <div className="p-5 border-t border-slate-800 flex justify-end gap-2">
                            <button
                                onClick={() => setEditing(null)}
                                className="px-4 py-2 text-sm bg-slate-800 hover:bg-slate-700 rounded-lg"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={save}
                                disabled={saving}
                                className="flex items-center gap-2 px-4 py-2 text-sm bg-primary/20 hover:bg-primary/30 border border-primary/40 text-primary rounded-lg font-bold uppercase tracking-wider disabled:opacity-50"
                            >
                                <Save size={14} />
                                Guardar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const FieldSummary = ({ fields }) => {
    const activeFields = (fields || [])
        .filter(field => field.active !== false)
        .sort((a, b) => (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0));

    if (activeFields.length === 0) {
        return <span className="text-xs text-slate-500">Sin campos activos</span>;
    }

    return (
        <div className="flex flex-wrap gap-1.5">
            {activeFields.map(field => {
                const isAssignTag = field.payload_target === 'assign_tag';
                const isRemoveTag = field.payload_target === 'remove_tag';
                
                if (isAssignTag) {
                    // Badge visual para etiquetas asignadas
                    const bgColor = field.field_value || '#808080';
                    const isLight = parseInt(bgColor.slice(1), 16) > 0x7FFFFF;
                    const textColor = isLight ? '#000000' : '#FFFFFF';
                    return (
                        <span
                            key={field.id || `${field.applies_when}-${field.field_name}`}
                            style={{ backgroundColor: bgColor, color: textColor }}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold"
                            title={`${contextLabel(field.applies_when)} · Asignar etiqueta: ${field.field_name}`}
                        >
                            <span className="opacity-70">{contextLabel(field.applies_when)}</span>
                            <span>{field.field_name}</span>
                        </span>
                    );
                }
                
                if (isRemoveTag) {
                    return (
                        <span
                            key={field.id || `${field.applies_when}-${field.field_name}`}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded border border-rose-500/30 bg-rose-500/10 text-xs text-rose-300"
                            title={`${contextLabel(field.applies_when)} · Quitar etiqueta: ${field.field_name}`}
                        >
                            <span className="opacity-70">{contextLabel(field.applies_when)}</span>
                            <X size={12} />
                            <span className="font-mono">{field.field_name}</span>
                        </span>
                    );
                }
                
                return (
                    <span
                        key={field.id || `${field.applies_when}-${field.field_name}`}
                        className="max-w-full inline-flex items-center gap-1 px-2 py-1 rounded border border-slate-700 bg-slate-950 text-xs text-slate-300"
                        title={`${field.payload_target} · ${field.field_name} = ${field.field_value}`}
                    >
                        <span className="text-slate-500">{contextLabel(field.applies_when)}</span>
                        <span className="font-mono truncate">{field.field_name}</span>
                    </span>
                );
            })}
        </div>
    );
};

const TagFieldSelector = ({ field, onChange, availableTags }) => {
    const [mode, setMode] = useState(field.field_name && availableTags.find(t => t.name === field.field_name) ? 'existing' : 'new');
    
    const handleModeChange = (newMode) => {
        setMode(newMode);
        if (newMode === 'new') {
            onChange({ field_name: '', field_value: '#808080' });
        }
    };
    
    const handleTagSelect = (tagName) => {
        const tag = availableTags.find(t => t.name === tagName);
        if (tag) {
            onChange({ field_name: tag.name, field_value: tag.color });
        }
    };
    
    return (
        <div className="col-span-2 flex flex-col gap-2">
            <select
                value={mode === 'existing' && field.field_name ? field.field_name : '_mode_' + mode}
                onChange={(e) => {
                    if (e.target.value === '_mode_new') {
                        handleModeChange('new');
                    } else {
                        setMode('existing');
                        handleTagSelect(e.target.value);
                    }
                }}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm"
            >
                <option value="_mode_new">➕ Crear nueva etiqueta</option>
                {availableTags.length > 0 && <option disabled>──────────</option>}
                {availableTags.map(tag => (
                    <option key={tag.id} value={tag.name}>
                        {tag.name}
                    </option>
                ))}
            </select>
            
            {mode === 'new' && (
                <div className="grid grid-cols-2 gap-2">
                    <input
                        type="text"
                        value={field.field_name || ''}
                        onChange={(e) => onChange({ ...field, field_name: e.target.value })}
                        placeholder="Nombre de etiqueta"
                        className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm"
                    />
                    <div className="flex gap-2">
                        <input
                            type="color"
                            value={field.field_value || '#808080'}
                            onChange={(e) => onChange({ ...field, field_value: e.target.value })}
                            className="w-16 h-[38px] bg-slate-900 border border-slate-700 rounded-lg cursor-pointer"
                        />
                        <input
                            type="text"
                            value={field.field_value || '#808080'}
                            onChange={(e) => onChange({ ...field, field_value: e.target.value })}
                            placeholder="#808080"
                            pattern="^#[0-9A-Fa-f]{6}$"
                            className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm font-mono"
                        />
                    </div>
                </div>
            )}
            
            {mode === 'existing' && field.field_name && (
                <div 
                    className="px-3 py-2 rounded-lg text-xs font-semibold inline-flex items-center gap-2 w-fit"
                    style={{ 
                        backgroundColor: field.field_value || '#808080',
                        color: parseInt((field.field_value || '#808080').slice(1), 16) > 0x7FFFFF ? '#000' : '#FFF'
                    }}
                >
                    {field.field_name}
                    <span className="opacity-70">{field.field_value}</span>
                </div>
            )}
        </div>
    );
};

const Select = ({ value, onChange, options }) => (
    <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm"
    >
        {options.map(option => (
            <option key={option.value} value={option.value}>{option.label}</option>
        ))}
    </select>
);

const Input = ({ value, onChange, placeholder }) => (
    <input
        type="text"
        value={value || ''}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm"
    />
);

const Field = ({ label, children }) => (
    <div>
        <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">
            {label}
        </label>
        {children}
    </div>
);

export default WhatsAppMessageAdRules;
